import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { MessageSquare, Search, ArrowLeft, Loader2, Inbox, RefreshCw, Send, Shield, Stethoscope, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

interface MessageRow {
  id: string;
  conversation_id: string;
  sender_id: string;
  recipient_id: string;
  subject: string | null;
  body: string;
  is_read: boolean;
  created_at: string;
}

interface ConversationSummary {
  conversation_id: string;
  subject: string | null;
  participant_ids: string[];
  participant_names: string[];
  total_messages: number;
  unread_count: number;
  last_message: string;
  last_message_at: string;
}

const AdminMessageOversight = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeConv, setActiveConv] = useState<string | null>(null);
  const [thread, setThread] = useState<MessageRow[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [profileCache, setProfileCache] = useState<Record<string, string>>({});
  const [roleCache, setRoleCache] = useState<Record<string, string>>({});
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  const resolveName = useCallback(
    async (userId: string): Promise<string> => {
      if (profileCache[userId]) return profileCache[userId];
      const [{ data: profileData }, { data: roleData }] = await Promise.all([
        supabase.from("profiles").select("first_name, last_name").eq("user_id", userId).single(),
        supabase.from("user_roles").select("role").eq("user_id", userId).limit(1),
      ]);
      const name = profileData
        ? `${profileData.first_name || ""} ${profileData.last_name || ""}`.trim() || userId.slice(0, 8)
        : userId.slice(0, 8);
      const role = roleData?.[0]?.role || "user";
      setProfileCache((prev) => ({ ...prev, [userId]: name }));
      setRoleCache((prev) => ({ ...prev, [userId]: role }));
      return name;
    },
    [profileCache]
  );

  const fetchConversations = useCallback(async () => {
    setLoading(true);
    const { data: allMessages, error } = await supabase
      .from("messages")
      .select("*")
      .order("created_at", { ascending: false });

    if (error || !allMessages) {
      setLoading(false);
      return;
    }

    const convMap = new Map<string, MessageRow[]>();
    for (const msg of allMessages) {
      const list = convMap.get(msg.conversation_id) || [];
      list.push(msg);
      convMap.set(msg.conversation_id, list);
    }

    const summaries: ConversationSummary[] = [];
    for (const [convId, msgs] of convMap) {
      const participantSet = new Set<string>();
      msgs.forEach((m) => {
        participantSet.add(m.sender_id);
        participantSet.add(m.recipient_id);
      });
      const participantIds = Array.from(participantSet);
      const participantNames = await Promise.all(participantIds.map(resolveName));
      const unread = msgs.filter((m) => !m.is_read).length;
      const latest = msgs[0];

      summaries.push({
        conversation_id: convId,
        subject: msgs.find((m) => m.subject)?.subject || null,
        participant_ids: participantIds,
        participant_names: participantNames,
        total_messages: msgs.length,
        unread_count: unread,
        last_message: latest.body,
        last_message_at: latest.created_at,
      });
    }

    summaries.sort(
      (a, b) => new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime()
    );
    setConversations(summaries);
    setLoading(false);
  }, [resolveName]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const openThread = async (convId: string) => {
    setActiveConv(convId);
    setThreadLoading(true);
    const { data } = await supabase
      .from("messages")
      .select("*")
      .eq("conversation_id", convId)
      .order("created_at", { ascending: true });
    setThread(data || []);
    setThreadLoading(false);
    setReplyText("");
  };

  const handleReply = async () => {
    if (!user || !activeConv || !replyText.trim()) return;
    const conv = conversations.find((c) => c.conversation_id === activeConv);
    if (!conv) return;

    // Find the other party (not the admin) to reply to
    const recipientId = conv.participant_ids.find((id) => id !== user.id) || conv.participant_ids[0];

    setSending(true);
    const { error } = await supabase.from("messages").insert({
      conversation_id: activeConv,
      sender_id: user.id,
      recipient_id: recipientId,
      body: replyText.trim(),
    });
    if (error) {
      toast({ title: "Error", description: "Could not send reply.", variant: "destructive" });
    } else {
      toast({ title: "Sent", description: "Reply delivered to patient." });
      setReplyText("");
      // Refresh thread
      const { data } = await supabase
        .from("messages")
        .select("*")
        .eq("conversation_id", activeConv)
        .order("created_at", { ascending: true });
      setThread(data || []);
      fetchConversations();
    }
    setSending(false);
  };

  const totalUnread = conversations.reduce((s, c) => s + c.unread_count, 0);
  const filtered = conversations.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      c.participant_names.some((n) => n.toLowerCase().includes(q)) ||
      (c.subject && c.subject.toLowerCase().includes(q)) ||
      c.last_message.toLowerCase().includes(q)
    );
  });

  // Thread detail view
  if (activeConv) {
    const conv = conversations.find((c) => c.conversation_id === activeConv);
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => setActiveConv(null)} className="gap-1">
          <ArrowLeft size={14} /> Back to all conversations
        </Button>
        <Card className="border-border">
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b border-border">
              <p className="font-semibold text-sm text-foreground">
                {conv?.subject || "Conversation"}
              </p>
              <p className="text-xs text-muted-foreground">
                Between: {conv?.participant_names.join(" & ")} · {conv?.total_messages} messages
              </p>
            </div>
            {threadLoading ? (
              <div className="p-8 flex justify-center">
                <Loader2 size={18} className="animate-spin text-muted-foreground" />
              </div>
            ) : (
              <ScrollArea className="max-h-[500px]">
                <div className="p-4 space-y-3">
                  {thread.map((msg) => {
                    const senderName = profileCache[msg.sender_id] || msg.sender_id.slice(0, 8);
                    const senderRole = roleCache[msg.sender_id];
                    const RoleIcon = senderRole === "admin" ? Shield : senderRole === "provider" ? Stethoscope : User;
                    const roleLabel = senderRole === "admin" ? "Admin" : senderRole === "provider" ? "Provider" : "Patient";
                    return (
                      <div key={msg.id} className="border-b border-border pb-3 last:border-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-foreground">{senderName}</span>
                            <span className={cn(
                              "inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full",
                              senderRole === "admin" ? "bg-accent text-accent-foreground" :
                              senderRole === "provider" ? "bg-primary/10 text-primary" :
                              "bg-muted text-muted-foreground"
                            )}>
                              <RoleIcon size={10} /> {roleLabel}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {!msg.is_read && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-400 text-amber-600">
                                Unread
                              </Badge>
                            )}
                            <span className="text-[10px] text-muted-foreground">
                              {new Date(msg.created_at).toLocaleString("en-US", {
                                month: "short",
                                day: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                              })}
                            </span>
                          </div>
                        </div>
                        {msg.subject && (
                          <p className="text-xs text-muted-foreground font-medium mb-0.5">{msg.subject}</p>
                        )}
                        <p className="text-sm text-foreground whitespace-pre-wrap">{msg.body}</p>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}

            {/* Admin reply input */}
            <div className="flex gap-2 px-4 py-3 border-t border-border">
              <Textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Reply as admin…"
                className="min-h-[40px] max-h-[100px] resize-none text-sm"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleReply();
                  }
                }}
              />
              <Button size="sm" onClick={handleReply} disabled={sending || !replyText.trim()} className="self-end">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Conversation list view
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-lg text-foreground">Patient Messages</h2>
          {totalUnread > 0 && (
            <Badge variant="destructive" className="text-xs">
              {totalUnread} unread
            </Badge>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={fetchConversations} className="gap-1">
          <RefreshCw size={14} /> Refresh
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search by name, subject, or content…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={20} className="animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-border">
          <CardContent className="p-8 text-center">
            <Inbox size={32} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              {search ? "No conversations match your search." : "No patient messages yet."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">Participants</TableHead>
                <TableHead>Subject / Last Message</TableHead>
                <TableHead className="w-[80px] text-center">Messages</TableHead>
                <TableHead className="w-[80px] text-center">Unread</TableHead>
                <TableHead className="w-[120px]">Last Activity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((conv) => (
                <TableRow
                  key={conv.conversation_id}
                  className={cn(
                    "cursor-pointer hover:bg-muted/50",
                    conv.unread_count > 0 && "bg-primary/5"
                  )}
                  onClick={() => openThread(conv.conversation_id)}
                >
                  <TableCell className="font-medium text-sm">
                    {conv.participant_names.join(" & ")}
                  </TableCell>
                  <TableCell>
                    <p className="text-sm font-medium text-foreground truncate max-w-[300px]">
                      {conv.subject || conv.last_message}
                    </p>
                    {conv.subject && (
                      <p className="text-xs text-muted-foreground truncate max-w-[300px]">
                        {conv.last_message}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-center text-sm">{conv.total_messages}</TableCell>
                  <TableCell className="text-center">
                    {conv.unread_count > 0 ? (
                      <Badge variant="destructive" className="text-[10px]">
                        {conv.unread_count}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">0</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(conv.last_message_at).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};

export default AdminMessageOversight;
